"use client";

import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, CheckCircle, XCircle, Smile, Frown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Theme_logo } from "@/lib/Images";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { toast } from "react-hot-toast";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import { cn } from "@/lib/utils";
import { loginSchema } from "@/utils/schema/loginSchema";
import { useLoginMutation } from "@/store/slices/apiSlice";
import { LOCAL_STORAGE_KEY, SHOW_MESSAGE } from "@/lib/constant/constant";
import { setUserInfo } from "@/store/slices";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [alertMessage, setAlertMessage] = useState(null);
  const [login, { isLoading }] = useLoginMutation();

  const form = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
    mode: "onChange",
  });

  const { setError } = form;

  useEffect(() => {
    if (alertMessage?.type === "success") {
      const from = location.state?.from?.pathname || "/dashboard";
      navigate(from, { replace: true });
    }
  }, [alertMessage, location, navigate]);

  const onSubmit = async (data) => {
    try {
      const payload = {
        email: data?.email,
        password: data?.password,
      };

      const res = await login(payload).unwrap();

      if (res?.response.statusEnum === 1) {
        toast.success("Successfully logged in");

        const dataRes = res?.response.data;

        if (data?.rememberMe) {
          localStorage.setItem(LOCAL_STORAGE_KEY.AUTH_TOKEN, dataRes.token);
          localStorage.setItem(
            LOCAL_STORAGE_KEY.USER_INFO,
            JSON.stringify(dataRes),
          );
        } else {
          sessionStorage.setItem(LOCAL_STORAGE_KEY.AUTH_TOKEN, dataRes.token);
          sessionStorage.setItem(
            LOCAL_STORAGE_KEY.USER_INFO,
            JSON.stringify(dataRes),
          );
        }

        dispatch(
          setUserInfo({
            info: dataRes,
            token: dataRes.token,
          }),
        );

        setAlertMessage({ type: "success" });
      } else {
        if (res?.response.errorCode === 164) {
          res?.response.data.sets.forEach((err) => {
            setError(err.key, {
              type: "manual",
              message: err.message,
            });
          });
        } else {
          setAlertMessage({
            message: res?.response.message,
            heading: SHOW_MESSAGE?.LOGIN_FAILED,
            type: "error",
          });
        }
      }
    } catch (err) {
      setAlertMessage({
        message: SHOW_MESSAGE?.SOMETHING_WENT_WRONG,
        heading: "Login Error",
        type: "error",
      });
      console.error("Login Error:", err);
    }
  };

  return (
    <div className="w-full max-w-xl px-4">
      <div className="text-center mb-10">
        <div className="flex justify-center mb-16">
          <img
            src={Theme_logo || "/placeholder.svg"}
            className="h-20"
            alt="Simstac Systems Logo"
          />
        </div>

        <h1 className="text-2xl font-bold text-foreground mb-4">
          Welcome Admin
        </h1>

        <p className="text-responsive-sm text-foreground/80">
          Please enter your registered email and password in the login fields.
        </p>
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-6 max-w-md mx-auto"
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel className="sr-only">Email address</FormLabel>

                <FormControl>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Enter your email"
                      error={fieldState.error}
                      className={cn(
                        "form-dark-input w-full h-12 pr-11 bg-white text-black",
                        fieldState.error
                          ? "border-red-500 focus-visible:ring-red-500"
                          : field.value
                            ? "border-green-500 focus-visible:ring-green-500"
                            : "",
                      )}
                      {...field}
                    />

                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {fieldState.error ? (
                        <Smile className="h-5 w-5 text-red-500" />
                      ) : field.value ? (
                        <Frown className="h-5 w-5 text-green-500" />
                      ) : null}
                    </div>
                  </div>
                </FormControl>

                <FormMessage className="text-red-500 text-sm mt-1" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel className="sr-only">Password</FormLabel>

                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      error={fieldState.error}
                      className="form-dark-input w-full h-12 pr-11 bg-white text-black"
                      {...field}
                    />

                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-0"
                    >
                      {showPassword ? (
                        <Eye className="h-5 w-5" />
                      ) : (
                        <EyeOff className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </FormControl>

                <FormMessage className="text-red-500 text-sm mt-1" />
              </FormItem>
            )}
          />

          <div className="flex items-center justify-between">
            <FormField
              control={form.control}
              name="rememberMe"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                      id="rememberMe"
                    />
                  </FormControl>

                  <FormLabel
                    htmlFor="rememberMe"
                    className="text-sm text-foreground cursor-pointer"
                  >
                    Remember Password
                  </FormLabel>
                </FormItem>
              )}
            />

            <Link
              to="/forgot-password"
              className="text-sm font-semibold text-[#1E9BFF] hover:underline"
            >
              Forgot password?
            </Link>
          </div>

          {alertMessage?.type === "error" && (
            <div className="p-3 text-responsive-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-md">
              {alertMessage.heading && (
                <strong className="block mb-1">{alertMessage.heading}</strong>
              )}
              {alertMessage.message}
            </div>
          )}

          <Button
            type="submit"
            className="w-full h-12 !mt-10"
            disabled={isLoading}
          >
            {isLoading ? "Signing In..." : "Sign in"}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default Login;
